import logger from './logger';
import cloneDeep from 'lodash/cloneDeep';
import {
  appendURLParam,
  getBackendSafeRoomName,
  parseURIString
} from '../util/uri';

import {
  CONNECTION_DISCONNECTED,
  CONNECTION_ESTABLISHED,
  CONNECTION_FAILED,
  CONNECTION_PROPERTIES_UPDATED,
  CONNECTION_WILL_CONNECT,
  SET_LOCATION_URL,
  SET_PREFER_VISITOR
} from './actionTypes';
import JitsiMeetJS, { JitsiConnectionEvents } from '../lib-jitsi-meet';
import { JITSI_CONNECTION_URL_KEY } from './constants';
import { redirect } from '../conference/actions';
import { getJaasJWT, isVpaasMeeting } from 'store/modules/jaas/functions';
import { getCustomerDetails } from 'store/modules/jaas/actions';
import { setJWT } from '../jwt/actions';
import { jitsiLocalStorage } from '@jitsi/js-utils';
import { parseURLParams } from '../util/parseURLParams';

/**
 * The error structure passed to the {@link connectionFailed} action.
 *
 * Note there was an intention to make the error resemble an Error instance (to
 * the extent that jitsi-meet needs it).
 */
// export type ConnectionFailedError = {

//     /**
//      * The invalid credentials that were used to authenticate and the
//      * authentication failed.
//      */
//     credentials?: {

//         /**
//          * The XMPP user's ID.
//          */
//         jid: string;

//         /**
//          * The XMPP user's password.
//          */
//         password: string;
//     };

//     /**
//      * The details about the connection failed event.
//      */
//     details?;

//     /**
//      * Error message.
//      */
//     message?: string;

//     /**
//      * One of {@link JitsiConnectionError} constants (defined in
//      * lib-jitsi-meet).
//      */
//     name: string;

//     /**
//      * Indicates whether this event is recoverable or not.
//      */
//     recoverable?: boolean;
// };

/**
 * Create an action for when the signaling connection has been lost.
 *
 * @param {JitsiConnection} connection - The {@code JitsiConnection} which
 * disconnected.
 * @private
 * @returns {{
 *     type: CONNECTION_DISCONNECTED,
 *     connection: JitsiConnection
 * }}
 */
export function connectionDisconnected(connection) {
  return {
    type: CONNECTION_DISCONNECTED,
    connection
  };
}

/**
 * Create an action for when the signaling connection has been established.
 *
 * @param {JitsiConnection} connection - The {@code JitsiConnection} which was
 * established.
 * @param {number} timeEstablished - The time at which the
 * {@code JitsiConnection} which was established.
 * @public
 * @returns {{
 *     type: CONNECTION_ESTABLISHED,
 *     connection: JitsiConnection,
 *     timeEstablished
 * }}
 */
export function connectionEstablished(connection, timeEstablished) {
  return {
    type: CONNECTION_ESTABLISHED,
    connection,
    timeEstablished
  };
}

/**
 * Create an action for when the signaling connection could not be created.
 *
 * @param {JitsiConnection} connection - The {@code JitsiConnection} which
 * failed.
 * @param {ConnectionFailedError} error - Error.
 * @public
 * @returns {{
 *     type: CONNECTION_FAILED,
 *     connection: JitsiConnection,
 *     error: ConnectionFailedError
 * }}
 */
export function connectionFailed(connection, error) {
  const { credentials } = error;

  if (credentials && !Object.keys(credentials).length) {
    error.credentials = undefined;
  }

  return {
    type: CONNECTION_FAILED,
    connection,
    error
  };
}

/**
 * Constructs options to be passed to the constructor of {@code JitsiConnection}
 * based on the redux state.
 *
 * @param {Object} state - The redux state.
 * @returns {Object} The options to be passed to the constructor of
 * {@code JitsiConnection}.
 */
export function constructOptions(state) {
  // Deep clone the options to make sure we don't modify the object in the
  // redux store.
  const options = cloneDeep(state['features/base/config']);

  const { locationURL, preferVisitor } = state['features/base/connection'];
  const params = parseURLParams(locationURL || '');
  const iceServersOverride = params['iceServers.replace'];

  if (iceServersOverride) {
    options.iceServersOverride = iceServersOverride;
  }
  let { bosh, websocket, flags } = options;

  // TESTING: Only enable WebSocket for some percentage of users.
  if (websocket && navigator.product === 'ReactNative') {
    if (Math.random() * 100 >= (options?.testing?.mobileXmppWsThreshold ?? 0)) {
      websocket = undefined;
    }
  }

  // Normalize the BOSH URL.
  if (bosh && !websocket) {
    if (bosh.startsWith('//')) {
      // By default our config.js doesn't include the protocol.
      bosh = `${locationURL?.protocol}${bosh}`;
    } else if (bosh.startsWith('/')) {
      // Handle relative URLs, which won't work on mobile.
      const { protocol, host, contextRoot } = parseURIString(locationURL?.href);

      bosh = `${protocol}//${host}${contextRoot || '/'}${bosh.substr(1)}`;
    }
  }

  // WebSocket is preferred over BOSH.
  const serviceUrl = websocket || bosh;

  logger.log(`Using service URL ${serviceUrl}`);
  options.serviceUrl = serviceUrl;
  // Append room to the URL's search.
  const { room } = state['features/base/conference'];

  if (serviceUrl && room) {
    const roomName = getBackendSafeRoomName(room);

    options.serviceUrl = appendURLParam(serviceUrl, 'room', roomName ?? '');

    if (options.websocketKeepAliveUrl) {
      options.websocketKeepAliveUrl = appendURLParam(
        options.websocketKeepAliveUrl,
        'room',
        roomName ?? ''
      );
    }

    if (options.conferenceRequestUrl) {
      options.conferenceRequestUrl = appendURLParam(
        options.conferenceRequestUrl,
        'room',
        roomName ?? ''
      );
    }
  }

  if (preferVisitor) {
    options.preferVisitor = true;
  }

  // Enable ssrc-rewriting by default.
  if (typeof flags?.ssrcRewritingEnabled === 'undefined') {
    const { ...otherFlags } = flags ?? {};

    options.flags = {
      ...otherFlags,
      ssrcRewritingEnabled: true
    };
  }
  return options;
}

/**
 * Sets the location URL of the application, connection, conference, etc.
 *
 * @param {URL} [locationURL] - The location URL of the application,
 * connection, conference, etc.
 * @returns {{
 *     type: SET_LOCATION_URL,
 *     locationURL: URL
 * }}
 */
export function setLocationURL(locationURL) {
  return {
    type: SET_LOCATION_URL,
    locationURL
  };
}

/**
 * To change prefer visitor in the store. Used later to decide what to request from jicofo on connection.
 *
 * @param {boolean} preferVisitor - The value to set.
 * @returns {{
 *     type: SET_PREFER_VISITOR,
 *     preferVisitor: boolean
 * }}
 */
export function setPreferVisitor(preferVisitor) {
  return {
    type: SET_PREFER_VISITOR,
    preferVisitor
  };
}

/**
 * Opens new connection.
 *
 * @param {string} [id] - The XMPP user's ID (e.g. {@code user@server.com}).
 * @param {string} [password] - The XMPP user's password.
 * @returns {Function}
 */
export function _connectInternal(id, password) {
  return (dispatch, getState) => {
    const state = getState();
    const options = constructOptions(state);
    const { locationURL } = state['features/base/connection'];
    const { jwt } = state['features/base/jwt'];

    const connection = new JitsiMeetJS.JitsiConnection(
      options.appId,
      jwt,
      options
    );

    connection[JITSI_CONNECTION_URL_KEY] = locationURL;
    // eslint-disable-next-line no-undef
    APP.connection = connection;
    dispatch(_connectionWillConnect(connection));

    return new Promise((resolve, reject) => {
      connection.addEventListener(
        JitsiConnectionEvents.CONNECTION_DISCONNECTED,
        _onConnectionDisconnected
      );
      connection.addEventListener(
        JitsiConnectionEvents.CONNECTION_ESTABLISHED,
        _onConnectionEstablished
      );
      connection.addEventListener(
        JitsiConnectionEvents.CONNECTION_FAILED,
        _onConnectionFailed
      );
      connection.addEventListener(
        JitsiConnectionEvents.CONNECTION_REDIRECTED,
        _onConnectionRedirected
      );
      connection.addEventListener(
        JitsiConnectionEvents.PROPERTIES_UPDATED,
        _onPropertiesUpdate
      );

      /**
       * Unsubscribe the connection instance from
       * {@code CONNECTION_DISCONNECTED} and {@code CONNECTION_FAILED} events.
       *
       * @returns {void}
       */
      function unsubscribe() {
        connection.removeEventListener(
          JitsiConnectionEvents.CONNECTION_DISCONNECTED,
          _onConnectionDisconnected
        );
        connection.removeEventListener(
          JitsiConnectionEvents.CONNECTION_FAILED,
          _onConnectionFailed
        );
        connection.removeEventListener(
          JitsiConnectionEvents.PROPERTIES_UPDATED,
          _onPropertiesUpdate
        );
      }

      /**
       * Dispatches {@code CONNECTION_DISCONNECTED} action when connection is
       * disconnected.
       *
       * @private
       * @returns {void}
       */
      function _onConnectionDisconnected() {
        unsubscribe();
        dispatch(connectionDisconnected(connection));
        resolve(connection);
      }

      /**
       * Rejects external promise when connection fails.
       *
       * @param {JitsiConnectionErrors} err - Connection error.
       * @param {string} [message] - Error message supplied by lib-jitsi-meet.
       * @param {Object} [credentials] - The invalid credentials that were
       * used to authenticate and the authentication failed.
       * @param {string} [credentials.jid] - The XMPP user's ID.
       * @param {string} [credentials.password] - The XMPP user's password.
       * @param {Object} details - Additional information about the error.
       * @private
       * @returns {void}
       */
      function _onConnectionFailed(err, message, credentials, details) {
        // eslint-disable-line max-params
        unsubscribe();

        dispatch(
          connectionFailed(connection, {
            credentials,
            details,
            name: err,
            message
          })
        );

        reject(err);
      }

      /**
       * Resolves external promise when connection is established.
       *
       * @private
       * @returns {void}
       */
      function _onConnectionEstablished() {
        connection.removeEventListener(
          JitsiConnectionEvents.CONNECTION_ESTABLISHED,
          _onConnectionEstablished
        );
        dispatch(connectionEstablished(connection, Date.now()));
        resolve(connection);
      }

      /**
       * Connection was redirected.
       *
       * @param {string|undefined} vnode - The vnode to connect to.
       * @param {string} focusJid - The focus jid to use.
       * @param {string|undefined} username - The username to use when joining. This is after promotion from
       * visitor to main participant.
       * @private
       * @returns {void}
       */
      function _onConnectionRedirected(vnode, focusJid, username) {
        connection.removeEventListener(
          JitsiConnectionEvents.CONNECTION_REDIRECTED,
          _onConnectionRedirected
        );
        dispatch(redirect(vnode, focusJid, username));
      }

      /**
       * Connection properties were updated.
       *
       * @param {Object} properties - The properties which were updated.
       * @private
       * @returns {void}
       */
      function _onPropertiesUpdate(properties) {
        dispatch(_propertiesUpdate(properties));
      }

      // in case of configured http url for conference request we need the room name
      const name = getBackendSafeRoomName(
        state['features/base/conference'].room
      );

      connection.connect({
        id,
        password,
        name
      });
    });
  };
}

/**
 * Create an action for when a connection will connect.
 *
 * @param {JitsiConnection} connection - The {@code JitsiConnection} which will
 * connect.
 * @private
 * @returns {{
 *     type: CONNECTION_WILL_CONNECT,
 *     connection: JitsiConnection
 * }}
 */
function _connectionWillConnect(connection) {
  return {
    type: CONNECTION_WILL_CONNECT,
    connection
  };
}

/**
 * Create an action for when connection properties are updated.
 *
 * @param {Object} properties - The properties which were updated.
 * @private
 * @returns {{
 *     type: CONNECTION_PROPERTIES_UPDATED,
 *     properties: Object
 * }}
 */
function _propertiesUpdate(properties) {
  return {
    type: CONNECTION_PROPERTIES_UPDATED,
    properties
  };
}

/**
 * Opens new connection.
 *
 * @returns {Promise<JitsiConnection>}
 */
export function connect(id, password) {
  return (dispatch, getState) => {
    const state = getState();
    const { jwt } = state['features/base/jwt'];
    const { iAmRecorder, iAmSipGateway } = state['features/base/config'];

    if (!iAmRecorder && !iAmSipGateway && isVpaasMeeting(state)) {
      return dispatch(getCustomerDetails())
        .then(() => {
          if (!jwt) {
            return getJaasJWT(state);
          }
        })
        .then((j) => j && dispatch(setJWT(j)))
        .then(() => dispatch(_connectInternal(id, password)));
    }

    // used by jibri
    const usernameOverride = jitsiLocalStorage.getItem(
      'xmpp_username_override'
    );
    const passwordOverride = jitsiLocalStorage.getItem(
      'xmpp_password_override'
    );

    if (usernameOverride && usernameOverride.length > 0) {
      id = usernameOverride; // eslint-disable-line no-param-reassign
    }
    if (passwordOverride && passwordOverride.length > 0) {
      password = passwordOverride; // eslint-disable-line no-param-reassign
    }

    return dispatch(_connectInternal(id, password));
  };
}

/**
 * Closes connection.
 *
 * @param {boolean} [requestFeedback] - Whether or not to attempt showing a
 * request for call feedback.
 * @returns {Function}
 */
export function disconnect(requestFeedback = false, feedbackTitle) {
  // XXX For web based version we use conference hanging up logic from the old
  // app.
  // eslint-disable-next-line no-undef
  return () => APP.conference.hangup(requestFeedback, feedbackTitle);
}
